import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

// Styles shared by the editor and anywhere the (server-cleaned) message is shown
const css = `
.cs-rich { font-size:15px; line-height:1.6; color:var(--text); word-break:break-word; }
.cs-rich p { margin:0 0 10px; }
.cs-rich ul, .cs-rich ol { margin:0 0 10px; padding-left:24px; }
.cs-rich h3 { font-size:17px; margin:0 0 8px; }
.cs-rich blockquote { margin:0 0 10px; padding-left:12px; border-left:3px solid var(--border-md); color:var(--muted); }
.cs-rich a { color:var(--blue); text-decoration:underline; }
.cs-editor:empty:before { content:attr(data-placeholder); color:var(--hint); pointer-events:none; }
.cs-editor:focus { outline:none; }
`;
export const RichStyles = () => <style>{css}</style>;

// Displays a message. The HTML has already been cleaned by the server (only simple formatting survives).
export const RichContent = ({ html, style }) => (
  <>
    <RichStyles/>
    <div className="cs-rich" style={style} dangerouslySetInnerHTML={{ __html: html || '' }}/>
  </>
);

// A small rich-text box: bold / italic / underline / lists / links. Pasting is plain text only, so
// nothing odd (colours, fonts, images) can sneak in from Word or a web page.
export const RichEditor = forwardRef(function RichEditor({ initialHtml, onChange, placeholder, minHeight = 240 }, ref) {
  const el = useRef(null);

  useEffect(() => {
    if (el.current) el.current.innerHTML = initialHtml || '';
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch {}
    // set once on mount — the box is uncontrolled so the caret never jumps
    // eslint-disable-next-line
  }, []);

  const emit = () => {
    const node = el.current;
    if (!node) return;
    if (!node.textContent.trim() && !node.querySelector('li')) { if (node.innerHTML) node.innerHTML = ''; }   // lets the placeholder come back
    onChange?.(node.innerHTML, node.innerText.trim());
  };

  useImperativeHandle(ref, () => ({
    focus: () => el.current?.focus(),
    exec: (cmd, value) => { el.current?.focus(); document.execCommand(cmd, false, value); emit(); },
    getHtml: () => el.current?.innerHTML || '',
    getText: () => el.current?.innerText.trim() || '',
    clear: () => { if (el.current) { el.current.innerHTML = ''; emit(); } },
  }));

  return (
    <>
      <RichStyles/>
      <div ref={el} className="cs-rich cs-editor" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label="Message"
        data-placeholder={placeholder} onInput={emit}
        onPaste={(e) => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
        style={{ minHeight, padding:'14px 16px' }}/>
    </>
  );
});

// The formatting buttons (sits in the compose window's bottom bar, like Gmail's)
export function FormatBar({ editor }) {
  const [on, setOn] = useState({});

  useEffect(() => {
    const read = () => {
      try { setOn({ bold: document.queryCommandState('bold'), italic: document.queryCommandState('italic'), underline: document.queryCommandState('underline'),
        ul: document.queryCommandState('insertUnorderedList'), ol: document.queryCommandState('insertOrderedList') }); } catch {}
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, []);

  const link = () => {
    const sel = window.getSelection();
    const saved = sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    let url = (window.prompt('Link address', 'https://') || '').trim();
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)/i.test(url)) url = /^[\w.-]+\.[a-z]{2,}/i.test(url) ? `https://${url}` : '';
    if (!url) { window.alert('That doesn’t look like a web address.'); return; }
    if (saved) { sel.removeAllRanges(); sel.addRange(saved); }
    editor.current?.exec('createLink', url);
  };

  const btn = (key, icon, label, run) => (
    <button type="button" key={label} title={label} aria-label={label} aria-pressed={!!on[key]}
      onMouseDown={e => e.preventDefault()}   // keeps the text selection while clicking
      onClick={run}
      style={{ width:'32px', height:'32px', display:'inline-flex', alignItems:'center', justifyContent:'center', border:'none', borderRadius:'6px', cursor:'pointer',
        background: on[key] ? 'var(--blue-light)' : 'transparent', color: on[key] ? 'var(--blue)' : 'var(--muted)' }}>
      <i className={`ti ${icon}`} style={{ fontSize:'17px' }} aria-hidden="true"/>
    </button>
  );
  const exec = (cmd) => () => editor.current?.exec(cmd);

  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'2px', flexWrap:'wrap' }}>
      {btn('bold', 'ti-bold', 'Bold', exec('bold'))}
      {btn('italic', 'ti-italic', 'Italic', exec('italic'))}
      {btn('underline', 'ti-underline', 'Underline', exec('underline'))}
      {btn('ul', 'ti-list', 'Bulleted list', exec('insertUnorderedList'))}
      {btn('ol', 'ti-list-numbers', 'Numbered list', exec('insertOrderedList'))}
      {btn('link', 'ti-link', 'Add link', link)}
      {btn('clear', 'ti-clear-formatting', 'Clear formatting', exec('removeFormat'))}
    </span>
  );
}
